import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useToast } from 'components/ToastManager';
import {
  useCreatePlatformOnboardingApplicationMutation,
  useGetPaymentPlatformEntityQuery,
} from 'hooks/internalApi';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { Suspense, useCallback, useMemo } from 'react';
import { getResultOrError } from 'utils/errors';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import {
  LayoutCell,
  LayoutRow,
} from '@fragment/ui/src/components/Payments/LayoutRow';
import { Toast } from '@fragment/ui/src/components/Toast';

const PaymentsPageContent = () => {
  const { context: internalApiCtx } = useInternalApiContext();
  const workspaceId = useWorkspaceId();
  const [, setLocation] = useLocation();
  const { showToast } = useToast();

  const [{ data: entityData, error: entityError }] =
    useGetPaymentPlatformEntityQuery({
      variables: { workspaceId },
      context: internalApiCtx,
    });

  const [{ fetching: creating }, createPlatformOnboardingApplication] =
    useCreatePlatformOnboardingApplicationMutation();

  const handleCreate = useCallback(async () => {
    const creationResult = await createPlatformOnboardingApplication(
      { workspaceId },
      internalApiCtx
    );
    const { result, isError, errorMessage } = getResultOrError(
      creationResult.data?.createPlatformOnboardingApplication,
      creationResult.error
    );
    if (isError) {
      showToast(<Toast message={errorMessage} type="error" />);
    } else {
      showToast(<Toast message="Application Started" type="success" />);
      setLocation(
        makePath(['w', workspaceId, 'onboarding'], { absolute: true })
      );
    }
  }, [
    workspaceId,
    createPlatformOnboardingApplication,
    internalApiCtx,
    showToast,
    setLocation,
  ]);

  const handleContinue = useCallback(() => {
    setLocation(makePath(['w', workspaceId, 'onboarding'], { absolute: true }));
  }, [workspaceId, setLocation]);

  const { result, isError, errorMessage } = getResultOrError(
    entityData?.paymentPlatformEntity,
    entityError
  );

  const content = useMemo(() => {
    if (isError) {
      if (errorMessage.includes('not found')) {
        return (
          <div className="flex flex-col gap-y-f2 p-f1">
            <span>No Application Found.</span>
            <PrimaryButtonV2 onClick={handleCreate} disabled={creating}>
              Create
            </PrimaryButtonV2>
          </div>
        );
      }
      return <div>Error: {errorMessage}</div>;
    }

    return <PrimaryButtonV2 onClick={handleContinue}>Continue</PrimaryButtonV2>;
  }, [isError, errorMessage, creating, handleCreate, handleContinue]);

  return (
    <>
      <LayoutRow height="small">
        <LayoutCell border="right">Application Status</LayoutCell>
        <LayoutCell border="none" width="full">
          {content}
        </LayoutCell>
      </LayoutRow>
    </>
  );
};
export const PaymentsPage = () => (
  <PaymentViewLayout title="Payment Settings">
    <Suspense fallback={<div>Loading...</div>}>
      <PaymentsPageContent />
    </Suspense>
  </PaymentViewLayout>
);
